<script setup lang="ts">
// DESIGN.md section 9.1, screen 2 — New match. Tap a side to make it active, scan or "Pick a
// name" fills it, format row once both sides are filled, Start. Same player both sides is
// rejected inline; a swap button flips the two.
import { computed, onMounted, onUnmounted, ref } from 'vue'
import TableDrawing from '../components/TableDrawing.vue'
import PlayerSearchList from '../components/PlayerSearchList.vue'
import LiveMatchBanner from '../components/LiveMatchBanner.vue'
import { draft, resetDraft, setSide, swapDraftSides } from '../store/draft'
import { navigate } from '../store/nav'
import { tableStore } from '../store/table'
import { settings } from '../store/settings'
import { createMatch, getMatchTypes, ApiRequestError } from '../backend'
import { onSttCommand, type SttCommandEvent } from '../stt'
import { resolveCornerClaim } from '../services/cornerClaim'
import { startConfirmation } from '../services/confirmFlow'
import { sounds } from '../sounds'
import { MATCH_TYPE_SERVE_EVERY_DEFAULTS, SERVE_EVERY_OPTIONS } from '../types'
import type { MatchType, Player, Side } from '../types'

const matchTypes = ref<MatchType[]>([])
const starting = ref(false)
const startError = ref<string | null>(null)
const pickerOpen = ref(false)

onMounted(async () => {
  try {
    const res = await getMatchTypes()
    matchTypes.value = res.items
    const current = matchTypes.value.find((mt) => mt.code === draft.matchTypeCode)
    if (current && !current.allowedBestOf.includes(draft.bestOf)) draft.bestOf = current.defaultBestOf
  } catch {
    matchTypes.value = []
  }
})

const currentMatchType = computed(() => matchTypes.value.find((mt) => mt.code === draft.matchTypeCode) ?? null)

const sameSide = computed(() => draft.left && draft.right && draft.left.id === draft.right.id)
const bothFilled = computed(() => !!draft.left && !!draft.right && !sameSide.value)
// Issue 1: same live-match state LiveMatchBanner (rendered below) surfaces — gates Start here
// too so a stale match on the table can't be walked straight back into the table_busy error;
// the banner's own Resume/Finalizar is the way out.
const tableHasLiveMatch = computed(() => !!tableStore.state?.liveMatch)

function selectSide(side: Side) {
  draft.activeSide = side
}

function pickMatchType(code: MatchType['code']) {
  draft.matchTypeCode = code
  const mt = matchTypes.value.find((m) => m.code === code)
  // Only fall back to the match type's own default when the CURRENT bestOf isn't valid for it
  // (mirrors the onMounted guard above) — 2026-08-31 user feedback item 1: the draft's best-of-3
  // default must survive tapping a format pill, not get silently reset to the match type's
  // (currently 1) default_best_of on every click.
  if (mt && !mt.allowedBestOf.includes(draft.bestOf)) draft.bestOf = mt.defaultBestOf
  // Round-3 feedback item 2: toggling the mode resets serves-every to THAT mode's default
  // (rapid/11-point -> 2 ITTF standard, classic 21 -> 3 house rule) — unconditionally, even if
  // the tapped pill was already active. A manual tap on the 2/3/5 row below is a plain
  // draft.serveEvery assignment (unaffected by this function), so it naturally stands until the
  // next mode toggle re-applies the default.
  draft.serveEvery = MATCH_TYPE_SERVE_EVERY_DEFAULTS[code]
}

function openPicker() {
  pickerOpen.value = true
}

function onPick(player: Player) {
  if (draft[draft.activeSide === 'left' ? 'right' : 'left']?.id === player.id) {
    startError.value = `${player.displayName} is already on the other side`
    setTimeout(() => (startError.value = null), 4000)
    pickerOpen.value = false
    return
  }
  setSide(draft.activeSide, player)
  draft.activeSide = draft.activeSide === 'left' ? 'right' : 'left'
  pickerOpen.value = false
}

function goBack() {
  navigate('home')
}

// ---------------------------------------------------------------------------------------------
// Round-16 (2026-08-31): "soy rojo"/"soy azul" (ES), "I am red"/"I am blue" (EN) — a speaker
// claims a table corner by voice (kmb_stt.py's corner_claim command). Round-22 adds the longer
// RECOMMENDED phrasing ("yo juego en el campo rojo/azul" / "i play on the red/blue side", ~2.5s
// vs ~1s — better x-vector embedding); both are the same corner_claim command client-side, no
// branching needed here. Red is ALWAYS the left
// card, blue ALWAYS the right one (same boxing-corner convention as LiveMatch's scoring) — the
// color word IS the side, no ambiguity. The x-vector paired with the claim (spkForCommand) is
// cosine-matched against EVERY enrolled player's centroid (not just the two already on the
// table, unlike LiveMatch's in-match speaker check — here we're figuring out WHO showed up) and
// the best match above threshold fills that side. Below threshold / no spk model / nobody
// enrolled close enough: the claim still shows a popup (so the room knows it was heard) but
// leaves the side for a manual pick.
const voicePopup = ref<{ text: string; ok: boolean } | null>(null)
let voicePopupTimer: ReturnType<typeof setTimeout> | null = null
function showVoicePopup(text: string, ok: boolean) {
  voicePopup.value = { text, ok }
  if (ok) sounds.voice()
  else sounds.voiceReject()
  if (voicePopupTimer) clearTimeout(voicePopupTimer)
  voicePopupTimer = setTimeout(() => (voicePopup.value = null), 3500)
}

// Brief glow on the claimed card (TableDrawing's leftGlow/rightGlow) — cleared a few seconds
// after the claim so it reads as a momentary confirmation, not a permanent state.
const leftGlow = ref(false)
const rightGlow = ref(false)
let glowTimer: ReturnType<typeof setTimeout> | null = null
function flashGlow(side: Side) {
  leftGlow.value = side === 'left'
  rightGlow.value = side === 'right'
  if (glowTimer) clearTimeout(glowTimer)
  glowTimer = setTimeout(() => {
    leftGlow.value = false
    rightGlow.value = false
  }, 3500)
}

// Round-23 (2026-08-31): the actual speaker-matching + tracking now lives in
// services/cornerClaim.ts (shared with App.vue's central hub, which needs the exact same
// resolution for Home's claim -> pre-assign -> New match flow). This screen just applies the
// result to its own UI (setSide + glow + popup), text unchanged from before the split.
async function handleCornerClaim(cmd: SttCommandEvent) {
  const result = await resolveCornerClaim(cmd, 'newMatch')
  if (!result) return
  if (result.outcome === 'accepted' && result.player) {
    setSide(result.side, result.player)
    flashGlow(result.side)
  }
  // Round-27: 'dubious' band — ask instead of assigning/dropping. Same global confirm flow as
  // Home (services/confirmFlow.ts, ConfirmClaimPrompt.vue mounted in App.vue) — only this
  // screen's onResolve differs (setSide + glow + this screen's own popup, instead of Home's
  // navigate + toast).
  if (result.outcome === 'needs_confirmation' && result.player) {
    const player = result.player
    startConfirmation({
      cmd,
      screen: 'newMatch',
      result,
      scope: result.scopeLabel,
      command: 'corner_claim',
      onResolve: (resolution, resolvedPlayer) => {
        if (resolution === 'confirmed') {
          setSide(result.side, resolvedPlayer)
          flashGlow(result.side)
          showVoicePopup(`${resolvedPlayer.displayName} → ${cmd.color === 'red' ? 'red' : 'blue'} corner (confirmed)`, true)
        } else if (resolution === 'denied') {
          showVoicePopup(`Not ${player.displayName} — try again`, false)
        }
        // timeout/cancelled: silent, per the standing false-negative-biased design.
      },
    })
    return
  }
  showVoicePopup(result.message, result.outcome === 'accepted')
}

let unsubVoice: (() => void) | null = null
onMounted(() => {
  unsubVoice = onSttCommand((cmd) => {
    if (cmd.command === 'corner_claim') void handleCornerClaim(cmd)
  })
})
onUnmounted(() => unsubVoice?.())

async function start() {
  if (!bothFilled.value || tableHasLiveMatch.value || !draft.left || !draft.right || starting.value) return
  starting.value = true
  startError.value = null
  try {
    const match = await createMatch({
      matchTypeCode: draft.matchTypeCode,
      bestOf: draft.bestOf,
      leftPlayerId: draft.left.id,
      rightPlayerId: draft.right.id,
      firstServer: draft.firstServer,
      serveEvery: draft.serveEvery,
      table: tableStore.table,
      // Round-8 feedback item 21 — set only when this screen was reached via a Tournament
      // bracket slot's "start" action (store/draft.ts's prefillFromTournamentSlot).
      tournamentSlotId: draft.tournamentSlotId ?? undefined,
      // Round-31 (2026-08-31) — the kiosk-wide Settings toggle (default ON), read once at
      // match start, not a per-match form field.
      fastMode: settings.fastMode,
    })
    // The draft's job is done — clear it so Home (which now drives the same store directly,
    // 2026-08-31) starts blank the next time a match finishes, instead of showing this match's
    // players pre-filled.
    resetDraft()
    navigate('live', { matchId: match.id })
  } catch (e) {
    if (e instanceof ApiRequestError) {
      if (e.code === 'table_busy') startError.value = 'This table already has a live match'
      else if (e.code === 'same_player') startError.value = 'Both sides have the same player'
      else startError.value = e.message
    } else {
      startError.value = 'Could not start the match'
    }
  } finally {
    starting.value = false
  }
}
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col gap-2 p-3 overflow-y-auto">
    <div class="flex items-center gap-3 shrink-0">
      <button type="button" class="tap-target px-4 rounded-xl bg-neutral-800 active:bg-neutral-700 font-semibold" @click="goBack">
        ← Home
      </button>
      <h1 class="text-lg font-bold flex-1">New match</h1>
      <!-- Round-8 feedback item 21: makes it visible that this New match was reached via a
           Tournament bracket "start" action (store/draft.ts's tournamentSlotId), not a plain
           manual match — the operator should know the result feeds a bracket. -->
      <span v-if="draft.tournamentSlotId != null" class="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-amber-900 text-amber-300">
        Tournament match
      </span>
    </div>

    <!-- Issue 1: same banner as Home — a live match on this table (voice "nueva partida", a
         direct hash link, etc. can all land here even though Home now gates its own Start) is
         surfaced with Resume/Finalizar instead of only showing up as a table_busy error on Start. -->
    <LiveMatchBanner />

    <!-- Round-3 feedback item 1: SAME TableDrawing component as Home, at the top — the swap pill
         (showSwap) replaces the old header "⇄ Swap" button so there's one swap affordance, not
         two. Still `active-side`-driven (tap a side to make it active, unchanged from before). -->
    <TableDrawing
      :left-player="draft.left"
      :right-player="draft.right"
      :active-side="draft.activeSide"
      interactive
      compact
      show-swap
      :left-glow="leftGlow"
      :right-glow="rightGlow"
      @select-side="selectSide"
      @swap="swapDraftSides"
    />

    <!-- Round-16: voice corner-claim feedback ("soy rojo"/"soy azul") — same popup pattern as
         LiveMatch's voice score/claim chip. -->
    <div
      v-if="voicePopup"
      class="shrink-0 rounded-xl px-4 py-2 text-sm font-semibold text-center"
      :class="voicePopup.ok ? 'bg-emerald-900/60 text-emerald-200 border border-emerald-700' : 'bg-neutral-900 text-neutral-400 border border-neutral-800'"
    >
      {{ voicePopup.text }}
    </div>

    <div class="flex gap-3 justify-center shrink-0">
      <button type="button" class="tap-target px-6 rounded-xl bg-neutral-800 active:bg-neutral-700 font-semibold" @click="openPicker">
        Pick a name
      </button>
    </div>

    <p v-if="sameSide" class="text-center text-red-400 font-semibold text-sm shrink-0">Same player can't be on both sides</p>
    <p v-if="startError" class="text-center text-red-400 font-semibold text-sm shrink-0">{{ startError }}</p>

    <div v-if="bothFilled" class="shrink-0 rounded-2xl bg-neutral-900 border border-neutral-800 p-3 flex flex-col gap-2">
      <!-- One row per setting (was a single wrapping row that could overflow with 2-word match
           type names + best-of buttons together). Segmented buttons here are smaller than the
           screen's usual 56px tap-target: they're a precision picker used up close right after
           badge scan, not a scored during-play control, so ~44px stays comfortably tappable
           while actually fitting three settings rows + Start at 960x540. -->
      <!-- Round-13: a tournament match is played in the TOURNAMENT's format (server-enforced
           in create_match) — the format/best-of/serves-every rows are hidden for those. -->
      <p v-if="draft.tournamentSlotId != null" class="text-xs text-neutral-500">
        Format set by the tournament.
      </p>
      <div v-if="draft.tournamentSlotId == null" class="flex items-center gap-3">
        <span class="text-[11px] font-bold text-neutral-400 uppercase tracking-wide w-24 shrink-0">Format</span>
        <div class="flex gap-2">
          <button
            v-for="mt in matchTypes"
            :key="mt.code"
            type="button"
            class="h-11 min-h-0 px-3 rounded-xl font-semibold text-xs"
            :class="draft.matchTypeCode === mt.code ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
            @click="pickMatchType(mt.code)"
          >
            {{ mt.name }}
          </button>
        </div>
      </div>

      <div v-if="draft.tournamentSlotId == null" class="flex items-center gap-3">
        <span class="text-[11px] font-bold text-neutral-400 uppercase tracking-wide w-24 shrink-0">Best of</span>
        <div class="flex gap-2">
          <button
            v-for="bo in currentMatchType?.allowedBestOf ?? [1, 3]"
            :key="bo"
            type="button"
            class="h-11 min-h-0 w-11 min-w-0 rounded-xl font-semibold text-sm"
            :class="draft.bestOf === bo ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
            @click="draft.bestOf = bo"
          >
            {{ bo }}
          </button>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <span class="text-[11px] font-bold text-neutral-400 uppercase tracking-wide w-24 shrink-0">Serves first</span>
        <div class="flex gap-2 min-w-0">
          <button
            type="button"
            class="h-11 min-h-0 px-3 rounded-xl font-semibold text-xs truncate max-w-[140px]"
            :class="draft.firstServer === 'left' ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
            @click="draft.firstServer = 'left'"
          >
            {{ draft.left?.displayName ?? 'Left' }}
          </button>
          <button
            type="button"
            class="h-11 min-h-0 px-3 rounded-xl font-semibold text-xs truncate max-w-[140px]"
            :class="draft.firstServer === 'right' ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
            @click="draft.firstServer = 'right'"
          >
            {{ draft.right?.displayName ?? 'Right' }}
          </button>
        </div>
      </div>

      <div v-if="draft.tournamentSlotId == null" class="flex items-center gap-3">
        <span class="text-[11px] font-bold text-neutral-400 uppercase tracking-wide w-24 shrink-0">Serves every</span>
        <div class="flex gap-2">
          <button
            v-for="n in SERVE_EVERY_OPTIONS"
            :key="n"
            type="button"
            class="h-11 min-h-0 w-11 min-w-0 rounded-xl font-semibold text-sm"
            :class="draft.serveEvery === n ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
            @click="draft.serveEvery = n"
          >
            {{ n }}
          </button>
        </div>
      </div>

      <button
        type="button"
        class="tap-target rounded-xl bg-emerald-600 active:bg-emerald-500 text-lg font-bold shadow-lg disabled:opacity-40"
        :disabled="starting || tableHasLiveMatch"
        @click="start"
      >
        {{ starting ? 'Starting…' : 'Start' }}
      </button>
    </div>

    <PlayerSearchList v-if="pickerOpen" @pick="onPick" @close="pickerOpen = false" />
  </div>
</template>
